import { log, logMethodCall } from './debug';
import { INT_RADIX, TOPIC_LAYER_VISITS_DEFAULT } from '../const/common';
import { getScrollPercentage } from './env';
import Event from '../const/event';

export function topicChecked(topic, subscribedTopics) {
  if (typeof subscribedTopics !== 'undefined' && subscribedTopics && subscribedTopics.length && (subscribedTopics || []).indexOf(topic._id) > -1) {
    return true;
  }
  return !topic.defaultUnchecked || (topic.matchPathChecked && topic.matchPathChecked && (location.pathname && (new RegExp(topic.matchPathChecked)).test(location.pathname)));
}

export function setTopics(context, topicIds, callback) {
  logMethodCall('setTopics', topicIds);

  return context.waitForInit().then(() => {
    if (!context.subscriptionManager) {
      return Promise.reject();
    }
    context.subscriptionManager.setTopics(topicIds);
    return context.subscriptionManager.sync().then(() => {
      if (typeof callback === 'function') {
        callback();
      }
    });
  });
}

export function getTopics(context, callback) {
  logMethodCall('getTopics');
  if (typeof callback !== 'function') {
    log.error('Please provide a callback function (err, topics) => {} to getTopics');
    return Promise.reject();
  }
  return context.waitForInit().then(() => {
    if (context.subscriptionManager) {
      context.subscriptionManager.getTopics().then((topics) => {
        callback(null, topics);
      });
    }
  });
}

/**
 * Show the topic layer for one topic
 * @param {*} topicParam Topic ID or Object
 * @param {*} optionsParam Options which can have `frequencyCappingVisits` setting
 */

export function showTopicLayer(topicParam, optionsParam, context) {
  context.waitForSubscription().then(() => {
    context.subscriptionManager.storageManager.getTopics().then((storedTopics) => {
      const topics = context.config.channelTopics || [];
      const topic = typeof topicParam === 'string' ? topics.find((currentTopic) => currentTopic._id === topicParam) : topicParam;
      if (!topic) {
        return;
      }

      // User already has subscribed to topic
      if (storedTopics.includes(topic._id)) {
        return;
      }

      const topicLayerShownKey = `cleverpush-topic-layerShown-${topic._id}`;
      const visitsStorageKey = `cleverpush-topic-layer-visits-${topic._id}`;

      // If options.frequencyCappingVisits is given and user has already seen layer before:
      // wait until minimum visis are reached
      let frequencyCappingVisits;
      if (typeof optionsParam !== 'undefined' && optionsParam.frequencyCappingVisits) {
        frequencyCappingVisits = optionsParam.frequencyCappingVisits;
        const layerShown = !!localStorage.getItem(topicLayerShownKey);
        if (layerShown) {
          const currentVisits = parseInt(localStorage.getItem(visitsStorageKey), INT_RADIX) || TOPIC_LAYER_VISITS_DEFAULT;
          // increment visits (localStorage only accepts strings)
          localStorage.setItem(visitsStorageKey, `${currentVisits + 1}`);
          // if visits are lower then minimum needed visits, exit here
          if (currentVisits < frequencyCappingVisits) {
            return;
          }
          localStorage.removeItem(visitsStorageKey);
        }
      }

      logMethodCall('showTopicLayer', topicParam);

      // I18n for the layer
      const customLocalization = {
        title: topic.layerTitle || `Möchtest du Neuigkeiten zu ${topic.name} erhalten?`,
        deny: topic.layerDenyText || 'Nein',
        allow: topic.layerAllowText || 'Ja',
        info: topic.layerInfo || ' '
      };
      Object.keys(customLocalization).forEach((key) => {
        if (typeof customLocalization[key] === 'undefined') {
          delete customLocalization[key];
        }
      });

      const hasChildTopics = topics.filter((childTopic) => childTopic.parentTopic === topic._id).length > 0;
      const layerTheme = topic.layerTheme || context.config.layerTheme || context.config.confirmAlertSelectTopicsLaterTheme || 'cleverpush-confirm-default';

      context.waitForConfirmAvailable().then(() => {
        context.subscriptionManager.confirm.appendConfirmBox(layerTheme, hasChildTopics, {
          topicsLayer: true,
          customLocalization,
          parentTopic: topic._id,
          topicsRadioButtons: topic.childTopicsRadioButtons,
          topicsRequired: topic.childTopicsRequired
        }, (allowed, selectedTopics) => {
          if (allowed && selectedTopics) {
            context.setTopics([...new Set([...storedTopics, ...selectedTopics, topic._id])]);
            localStorage.setItem(topicLayerShownKey, `${Date.now()}`);
          } else if (!frequencyCappingVisits) {
            sessionStorage.setItem(topicLayerShownKey, `${Date.now()}`);
          } else {
            localStorage.setItem(topicLayerShownKey, `${Date.now()}`);
          }
        });
      });
    });
  });
}

export function initTopics(context) {
  context.waitForSubscription().then(() => {
    context.subscriptionManager.storageManager.getTopics().then((storedTopics) => {
      const topics = context.config.channelTopics;
      if (!topics?.length || typeof localStorage === 'undefined') {
        return;
      }

      let topicFound = false;
      topics.forEach((topic) => {
        const topicLayerShownKey = `cleverpush-topic-layerShown-${topic._id}`;
        const visitsStorageKey = `cleverpush-topic-layer-visits-${topic._id}`;

        let topicMatches = topic.layerMatchPath && new RegExp(topic.layerMatchPath).test(location.pathname);
        if (topic.layerFunction && topicMatches) {
          try {
            topicMatches = !!eval(topic.layerFunction);
          } catch (err) {
            log.debug(err);
          }
        }

        if (
          topic.layerEnabled
          && !localStorage.getItem(topicLayerShownKey)
          && !sessionStorage.getItem(topicLayerShownKey)
          && !storedTopics.includes(topic._id)
          && topicMatches
        ) {
          if (topicFound) {
            return;
          }

          context.subscriptionManager.triggerEvent(Event.TOPICS_LAYER_SHOWN, topic._id);

          const finalizeShow = () => {
            showTopicLayer(topic, undefined, context);
          };

          const show = () => {
            topicFound = true;

            if (!isNaN(topic.layerScrollPercentage) && topic.layerScrollPercentage > 0) {
              const checkScroll = () => {
                if (getScrollPercentage() >= topic.layerScrollPercentage) {
                  window.removeEventListener('scroll', checkScroll);
                  finalizeShow();
                }
              };
              window.addEventListener('scroll', checkScroll);
              checkScroll();
            } else {
              finalizeShow();
            }
          };

          const visitsNeeded = !isNaN(topic.layerMinimumVisits) ? topic.layerMinimumVisits : 0;
          const visitsStorage = localStorage;
          const visitsStr = visitsStorage.getItem(visitsStorageKey);
          let visits = 0;
          if (typeof visitsStr === 'string' && !isNaN(visitsStr)) {
            visits = parseInt(visitsStr, INT_RADIX);
          }
          if (visits >= visitsNeeded) {
            if (!isNaN(topic.layerTimeout) && topic.layerTimeout) {
              setTimeout(() => {
                show();
              }, topic.layerTimeout * 1000);
            } else {
              show();
            }
          } else {
            visits += 1;
            visitsStorage.setItem(visitsStorageKey, visits.toString());
          }
        }
      });
    });
  });
}
